/**
 * scene_group 英文枚举 → 中文展示名。
 * 与后端 simulation_platform/utils/scene_classifier.py 的分类规则输出值对齐；
 * 未在此表登记的英文值原样展示（防止漏映射，也便于新增分组灰度）。
 *
 * 场景管理页（左侧树 daily 分组虚拟节点、任务列表「场景集合」列展示、
 * 创建 Job 弹窗 TreeSelect 分组子节点）共用这份映射。
 */
export const SCENE_GROUP_LABELS: Record<string, string> = {
  detour_problem: '绕行问题',
  change_lanes: '变道问题',
  abnormal_speed: '速度异常',
  quick_or_heavy_brake: '急刹 / 重刹',
  abnormal_braking: '异常制动',
  safety_distance: '安全距离',
  collision: '碰撞',
  parking: '泊车',
  follow: '跟车',
  cruise: '巡航',
  ring_problem: '环岛',
  traffic_lights: '红绿灯',
  abnormal_trajectory: '轨迹异常',
  follow_problem: '跟车问题',
  turning_problem: '转弯问题',
  carrow_road: '窄路通行',
  confluence_or_diversion: '汇流 / 分流',
  traverse_problem: '穿行问题',
  pile_crossing: '过桩问题',
  yield_pedestrians: '避让行人',
  uturn: '调头',
  other: '其他',
};

/**
 * 与后端 sentinel 一致：scene_group 为 NULL/空串时归到「未分类」桶。
 * 前后端 (job_service._parse_scene_set_tokens / query_scene_group_stats)
 * 都用同一个值，改动时同步。
 */
export const UNCATEGORIZED_KEY = '__uncategorized__';

/** 英文 group → 中文展示；未映射的原样返回；空/未分类 sentinel → 「未分类」。 */
export function sceneGroupDisplay(key: string | null | undefined): string {
  if (!key || key === UNCATEGORIZED_KEY) return '未分类';
  return SCENE_GROUP_LABELS[key] ?? key;
}

/**
 * 前端 tree checkbox 用的分组虚拟节点 key 编码 → 后端 sceneSetIds 里的 token 编码。
 * 两套编码历史原因不同（antd Tree 组件内部 key 更明确一点，后端追求 varchar 紧凑），
 * 一处切换收口在这里。
 *   TreeNode key:  group:<leafId>:<groupKey>
 *   Backend token: L<leafId>G:<groupKey>
 */
export const GROUP_TREE_KEY_PREFIX = 'group:';
export const GROUP_BACKEND_TOKEN_RE = /^L(\d+)G:(.+)$/;

export function encodeGroupTreeKey(leafId: number, groupKey: string): string {
  return `${GROUP_TREE_KEY_PREFIX}${leafId}:${groupKey}`;
}

export function decodeGroupTreeKey(key: unknown): { leafId: number; groupKey: string } | null {
  if (typeof key !== 'string' || !key.startsWith(GROUP_TREE_KEY_PREFIX)) return null;
  const rest = key.slice(GROUP_TREE_KEY_PREFIX.length);
  const idx = rest.indexOf(':');
  if (idx <= 0) return null;
  const leafId = Number(rest.slice(0, idx));
  const groupKey = rest.slice(idx + 1);
  if (!Number.isFinite(leafId) || !groupKey) return null;
  return { leafId, groupKey };
}

export function encodeGroupBackendToken(leafId: number, groupKey: string): string {
  return `L${leafId}G:${groupKey}`;
}

export function decodeGroupBackendToken(token: string): { leafId: number; groupKey: string } | null {
  const m = GROUP_BACKEND_TOKEN_RE.exec(token);
  if (!m) return null;
  return { leafId: Number(m[1]), groupKey: m[2] };
}
